import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { useFocusNext } from "../../../hooks/useFocusNext";
import { Row } from "react-grid-system";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Body from "../../atoms/typography/Body";
import BodySmall from "../../atoms/typography/BodySmall";
import ToggleButton from "../../atoms/buttons/ToggleButton";
import { ViewOffIcon, ViewOnIcon } from "../../atoms/icons/ViewIcons";
import IconButton from "../../atoms/buttons/IconButton";
import { ClearIcon } from "../../atoms/icons/ClearIcon";
import Divider from "../../atoms/Divider";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import { AddIcon } from "../../atoms/icons/AddIcon";
import DeleteModal from "../../molecules/modals/DeleteModal";
import RadioButton from "../../atoms/inputs/RadioButton";
import { theme } from "../../../theme";
import type { PossibleFieldPositionWithValue } from "../../../models/ClientInvoiceReaderTemplateModel";
import type { TemplateMaskInput } from "../../../models/InvoiceInterpretationInputs";
import type { EditableMask } from "../../../models/Templates";
import type { Position } from "../../../models/Position";
import type { CustomHighlight } from "../PdfTemplate";
import {
  resolvePositionWithValue,
  computeHighlightsForMask,
  buildMaskFieldHighlights,
  toTemplateMaskInput,
  buildFieldOptions,
} from "../../../utils/templateHelpers";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Alert from "../../molecules/Alert";
import { useUpdateTemplateMasks } from "../../../graphql/hooks/invoiceInterpretationTemplates/useUpdateTemplateMasks";
import type { TemplateTabHandle } from "../../../models/Templates";
import AutocompleteSelect from "../../atoms/inputs/AutocompleteSelect";
import ToggleAllTextButton from "../../atoms/buttons/ToggleAllTextButton";
import ActiveFieldWrapper from "./ActiveFieldWrapper";

const FlexedDivCentered = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

interface Props {
  possibleFields: Array<PossibleFieldPositionWithValue>;
  highlightedFields: CustomHighlight[];
  setHighlightedFields: React.Dispatch<React.SetStateAction<CustomHighlight[]>>;
  maskingData: EditableMask[];
  onUpdateMaskingDataState: React.Dispatch<
    React.SetStateAction<EditableMask[]>
  >;
  onUpdateActiveMaskId?: (id: string | null) => void;
  activeMaskId?: string | null;
  onSaveAndContinue: () => void;
  onDirtyChange?: () => void;
  onNavigateToPage?: (page: number) => void;
  totalPages?: number;
}

const EditMasks = forwardRef<TemplateTabHandle, Props>(
  (
    {
      possibleFields,
      highlightedFields,
      setHighlightedFields,
      maskingData,
      onUpdateMaskingDataState,
      onUpdateActiveMaskId,
      activeMaskId,
      onSaveAndContinue,
      onDirtyChange,
      onNavigateToPage,
      totalPages,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { id: clientIdentifier, templateId } = useParams();
    const [dirty, setDirty] = useState(false);
    const dirtyRef = useRef(false);
    const navigateAfterSaveRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const { formRef, lastFocusedInputRef, focusNextInput } = useFocusNext();
    const [activeField, setActiveField] = useState<{ index: number; field: "label" | "value" } | null>(null);
    const activeFieldRef = useRef<{ index: number; field: "label" | "value" } | null>(null);
    activeFieldRef.current = activeField;
    const maskingDataRef = useRef(maskingData);
    maskingDataRef.current = maskingData;

    const markDirty = (isDirty: boolean) => {
      dirtyRef.current = isDirty;
      setDirty(isDirty);
      onDirtyChange?.();
    };

    const toggleHighlight = (
      index: number,
      label?: EditableMask["label"],
      value?: EditableMask["value"],
    ) => {
      const mask = maskingData[index];
      const labelLabel = `mask-${index}-label`;
      const valueLabel = `mask-${index}-value`;

      setHighlightedFields((prev: CustomHighlight[]) => {
        const hasLabel = prev.some((h) => h.label === labelLabel);
        const hasValue = prev.some((h) => h.label === valueLabel);

        // Toggle off if any highlight exists, for either mode
        if (hasLabel || hasValue) {
          return prev.filter(
            (h) => h.label !== labelLabel && h.label !== valueLabel,
          );
        }

        const isDrawMode = mask.maskingType !== "REMOVE_LABEL_AND_VALUE";
        const newHighlights: CustomHighlight[] = [];

        if (label?.position) {
          newHighlights.push(
            ...buildMaskFieldHighlights(labelLabel, label, possibleFields, false),
          );
        }

        if (value?.position) {
          // Draw-mode value highlight is tracking-only; the drawn box itself
          // renders via maskOverlays.
          newHighlights.push(
            ...buildMaskFieldHighlights(
              valueLabel,
              value,
              possibleFields,
              isDrawMode,
            ),
          );
        }

        return [...prev, ...newHighlights];
      });
    };

    const deriveMaskingType = (
      mask: EditableMask,
    ): EditableMask["maskingType"] => {
      const isDraw = mask.maskingType !== "REMOVE_LABEL_AND_VALUE";
      const hasLabel = !!mask.label;

      if (!isDraw) return "REMOVE_LABEL_AND_VALUE";

      if (isDraw && hasLabel)
        return "REMOVE_ABSOLUTE_POSITION_ONLY_WHERE_LABEL_EXISTS";

      return "REMOVE_ABSOLUTE_POSITION_ON_EVERY_PAGE";
    };

    const updateMaskField = (
      index: number,
      field: "label" | "value",
      selectedValue?: string,
    ) => {
      markDirty(true);

      const resolved = selectedValue
        ? resolvePositionWithValue(selectedValue, possibleFields)
        : undefined;

      onUpdateMaskingDataState((prevMasks) => {
        const updatedMasks = prevMasks.map((mask, i) => {
          if (i !== index) return mask;

          const updatedMask = {
            ...mask,
            [field]: resolved,
          };

          return {
            ...updatedMask,
            maskingType: deriveMaskingType(updatedMask),
          };
        });

        setHighlightedFields(
          computeHighlightsForMask(
            updatedMasks[index],
            index,
            highlightedFields,
            possibleFields,
          ),
        );

        return updatedMasks;
      });

      if (resolved) {
        onNavigateToPage?.(resolved.pageNumber);
      }
    };

    const defaultPosition: Position = {
      bottomLeftX: 20,
      bottomLeftY: 20,
      topRightX: 80,
      topRightY: 40,
    };

    const setMaskPositionType = (
      index: number,
      type: "Draw" | "FieldBased",
    ) => {
      const mask = maskingData[index];

      onUpdateMaskingDataState((prev) => {
        const updatedMasks = prev.map((m, i): EditableMask => {
          if (i !== index) return m;

          if (type === "Draw") {
            const hasLabel = !!m.label;

            return {
              ...m,
              maskingType: hasLabel
                ? ("REMOVE_ABSOLUTE_POSITION_ONLY_WHERE_LABEL_EXISTS" as const)
                : ("REMOVE_ABSOLUTE_POSITION_ON_EVERY_PAGE" as const),
              value: m.value
                ? {
                    ...m.value,
                    position: m.value.position ?? defaultPosition,
                  }
                : { position: defaultPosition, value: "" },
            };
          } else {
            return {
              ...m,
              maskingType: "REMOVE_LABEL_AND_VALUE" as const,
            };
          }
        });

        // Re-compute highlights for the new mode
        setHighlightedFields(
          computeHighlightsForMask(
            updatedMasks[index],
            index,
            highlightedFields,
            possibleFields,
          ),
        );

        return updatedMasks;
      });
      markDirty(true);

      // Auto-activate when switching to Draw mode
      if (type === "Draw" && onUpdateActiveMaskId) {
        onUpdateActiveMaskId(mask.uiId);
      }
    };

    const addMask = () => {
      const newMask: EditableMask = {
        uiId: crypto.randomUUID(),
        maskingType: "REMOVE_LABEL_AND_VALUE",
        label: undefined,
        value: undefined,
      };
      markDirty(true);
      onUpdateMaskingDataState((prev) => [...prev, newMask]);
      // Auto-activate the new mask
      if (onUpdateActiveMaskId) {
        onUpdateActiveMaskId(newMask.uiId);
      }
    };

    const deleteMask = (index: number) => {
      markDirty(true);

      // Remove highlights for this mask
      const labelLabel = `mask-${index}-label`;
      const valueLabel = `mask-${index}-value`;
      setHighlightedFields((prev) =>
        prev.filter((h) => h.label !== labelLabel && h.label !== valueLabel),
      );

      onUpdateMaskingDataState((prev) => {
        const removed = prev[index];
        const next = prev.filter((_, i) => i !== index);

        if (removed.uiId === activeMaskId) {
          onUpdateActiveMaskId?.(null);
        }

        return next;
      });
    };

    const { updateTemplateMasks, loading } = useUpdateTemplateMasks({
      onError: (e) => {
        setError(e?.message || t("errors.somethingWentWrong"));
      },
      onCompleted: (result) => {
        if (result.invoiceInterpretation?.updateTemplateMasks?.success) {
          onUpdateActiveMaskId?.(null);
          if (navigateAfterSaveRef.current) { onSaveAndContinue(); }
        } else {
          setError(t("errors.somethingWentWrong"));
        }
      },
    });

    const save = async () => {
      if (!dirtyRef.current) return;

      setError(null);

      const masks: TemplateMaskInput[] = maskingData
        .map(toTemplateMaskInput)
        .filter((m): m is TemplateMaskInput => m !== null);

      await updateTemplateMasks({
        masks,
        templateId,
        clientIdentifier,
      });

      markDirty(false);
    };

    const areAllHighlighted = () => {
      // If no masks have positions, return false
      const masksWithPositions = maskingData.filter(
        (mask) => mask.label?.position || mask.value?.position,
      );
      if (masksWithPositions.length === 0) return false;

      // Check if all masks with positions are highlighted
      return masksWithPositions.every((_, index) => {
        const hasLabel = highlightedFields.some(
          (h) => h.label === `mask-${index}-label`,
        );
        const hasValue = highlightedFields.some(
          (h) => h.label === `mask-${index}-value`,
        );
        return hasLabel || hasValue;
      });
    };

    const toggleAll = () => {
      if (areAllHighlighted()) {
        // Hide all - remove all mask highlights
        const labelsToRemove = maskingData.flatMap((_, index) => [
          `mask-${index}-label`,
          `mask-${index}-value`,
        ]);
        setHighlightedFields(
          highlightedFields.filter(
            (h) => h.label && !labelsToRemove.includes(h.label),
          ),
        );
      } else {
        // Show all - add highlights for all masks
        const newHighlights: CustomHighlight[] = [];
        maskingData.forEach((mask, index) => {
          const labelLabel = `mask-${index}-label`;
          const valueLabel = `mask-${index}-value`;

          if (mask.label?.position) {
            newHighlights.push(
              ...buildMaskFieldHighlights(
                labelLabel,
                mask.label,
                possibleFields,
                false,
              ),
            );
          }

          if (mask.value?.position) {
            newHighlights.push(
              ...buildMaskFieldHighlights(
                valueLabel,
                mask.value,
                possibleFields,
                mask.maskingType !== "REMOVE_LABEL_AND_VALUE",
              ),
            );
          }
        });

        // Remove existing mask highlights and add new ones
        const labelsToRemove = maskingData.flatMap((_, index) => [
          `mask-${index}-label`,
          `mask-${index}-value`,
        ]);
        const filtered = highlightedFields.filter(
          (h) => h.label && !labelsToRemove.includes(h.label),
        );
        setHighlightedFields([...filtered, ...newHighlights]);
      }
    };

    const onClickSaveAndContinue = async () => {
      navigateAfterSaveRef.current = true;
      try {
        await save();
      } catch (e: any) {
        setError(e.message || t("errors.somethingWentWrong"));
      } finally {
        navigateAfterSaveRef.current = false;
      }
    };

    useImperativeHandle(ref, () => ({
      save,
      isDirty: () => dirtyRef.current,
      markDirty: () => markDirty(true),
      handleFieldClick: (clickedField) => {
        const active = activeFieldRef.current;
        if (!active || !clickedField.value) return;
        updateMaskField(active.index, active.field, clickedField.value);
        setActiveField(null);
        focusNextInput();
      },
    }));

    return (
      <div ref={formRef}>
        {error && <Alert type="error" title={error} />}
        {maskingData && maskingData.length > 0 && (
          <>
            <HorizontalSpacer spacing={1} />
            <Row nogutter justify="end">
              <ToggleAllTextButton
                onClick={toggleAll}
                showHideAll={areAllHighlighted()}
                showAllText={t("templates.showAll")}
                hideAllText={t("templates.hideAll")}
              />
            </Row>
            <HorizontalSpacer spacing={1} />
          </>
        )}
        {maskingData.map((mask, index) => (
          <div key={mask.uiId}>
            <Row nogutter justify="between" align="center">
              <HorizontalSpacer spacing={0.5} />
              <Body>
                {t("templates.mask")} {mask.label?.value}
              </Body>
              <div style={{ display: "flex", gap: 24 }}>
                <ToggleButton
                  onToggle={() =>
                    toggleHighlight(index, mask.label, mask.value)
                  }
                  isOn={
                    highlightedFields.some(
                      (h) => h.label === `mask-${index}-label`,
                    ) ||
                    highlightedFields.some(
                      (h) => h.label === `mask-${index}-value`,
                    )
                  }
                  size="medium"
                  iconOn={<ViewOnIcon color={theme.colors.white} />}
                  iconOff={<ViewOffIcon color={theme.colors.primary} />}
                  tabIndex={-1}
                />

                <DeleteModal
                  deleteButtonType="iconButton"
                  onClickDelete={() => deleteMask(index)}
                />
              </div>
            </Row>
            <HorizontalSpacer spacing={0.5} />
            <FlexedDivCentered>
              <RadioButton
                title={t("templates.maskField")}
                checked={mask.maskingType === "REMOVE_LABEL_AND_VALUE"}
                onChange={() => setMaskPositionType(index, "FieldBased")}
              />
              <RadioButton
                title={t("templates.draw")}
                checked={mask.maskingType !== "REMOVE_LABEL_AND_VALUE"}
                onChange={() => setMaskPositionType(index, "Draw")}
              />
            </FlexedDivCentered>
            <HorizontalSpacer spacing={0.5} />
            <FlexedDivCentered style={{ gap: 32, alignItems: "flex-start" }}>
              <div style={{ width: "100%" }}>
                <BodySmall>{t("templates.label")}</BodySmall>
                <FlexedDivCentered>
                  <ActiveFieldWrapper
                    $active={activeField?.index === index && activeField?.field === "label"}
                    onClick={() => setActiveField({ index, field: "label" })}
                    onFocus={(e) => { setActiveField({ index, field: "label" }); if (e.target instanceof HTMLInputElement) lastFocusedInputRef.current = e.target; }}
                  >
                    <AutocompleteSelect
                      value={mask.label?.value || ""}
                      onChange={(value) => { updateMaskField(index, "label", value); setActiveField(null); focusNextInput(); }}
                      options={buildFieldOptions(possibleFields, totalPages, t("common.page"), t("templates.selectAField"))}
                    />
                  </ActiveFieldWrapper>
                  <IconButton
                    icon={<ClearIcon />}
                    onClick={() => updateMaskField(index, "label", undefined)}
                    tabIndex={-1}
                  />
                </FlexedDivCentered>
              </div>

              {mask.maskingType === "REMOVE_LABEL_AND_VALUE" && (
                <div style={{ width: "100%" }}>
                  <BodySmall>{t("templates.value")}</BodySmall>
                  <FlexedDivCentered>
                    <ActiveFieldWrapper
                      $active={activeField?.index === index && activeField?.field === "value"}
                      onClick={() => setActiveField({ index, field: "value" })}
                      onFocus={(e) => { setActiveField({ index, field: "value" }); if (e.target instanceof HTMLInputElement) lastFocusedInputRef.current = e.target; }}
                    >
                      <AutocompleteSelect
                        value={mask.value?.value || ""}
                        onChange={(value) => { updateMaskField(index, "value", value); setActiveField(null); focusNextInput(); }}
                        options={buildFieldOptions(possibleFields, totalPages, t("common.page"), t("templates.selectAField"))}
                      />
                    </ActiveFieldWrapper>
                    <IconButton
                      icon={<ClearIcon />}
                      onClick={() => updateMaskField(index, "value", undefined)}
                      tabIndex={-1}
                    />
                  </FlexedDivCentered>
                </div>
              )}
            </FlexedDivCentered>
            <HorizontalSpacer spacing={1} />
            <Divider />
          </div>
        ))}
        <HorizontalSpacer spacing={2} />
        <OutlinedButton
          color={theme.colors.primary}
          medium
          title={t("templates.addMask")}
          icon={<AddIcon color={theme.colors.primary} />}
          onClick={addMask}
        />

        <HorizontalSpacer spacing={2} />
        <Divider />
        <HorizontalSpacer spacing={2} />
        <Row nogutter justify="end">
          <OutlinedButton
            title={t("random.saveAndContinue")}
            iconRight={<ChevronSmallRightIcon color={theme.colors.primary} />}
            color={theme.colors.primary}
            medium
            onClick={onClickSaveAndContinue}
            disabled={loading || !dirty}
          />
        </Row>
      </div>
    );
  },
);

export default EditMasks;
